import React, { useEffect, useMemo, useRef, useState } from 'react';
import './index.css';
import {useDispatch, useSelector} from "react-redux";
import {useTranslation} from "../../../../hooks/useTranslation";
import {setShowReleaseNotesModal} from "../../../../_reducers/activitySlice";
import {Accordion, Button, Form, Modal, Spinner} from "react-bootstrap";
import {getCurrentUser, isEmpty} from "../../../../_helpers/commonFunctions";
import moment from "moment";
import DateBox from "../../DateBox";
import {FaBug, FaCashRegister} from "react-icons/fa";
import {IoChevronForwardSharp, IoDocumentTextOutline, IoSparkles} from "react-icons/io5";
import {IoMdTrendingUp} from "react-icons/io";
import {BsPlugin} from "react-icons/bs";
import {GrCloudSoftware} from "react-icons/gr";
import {CiCalendar, CiCalendarDate, CiSearch} from "react-icons/ci";
import {MdOutlineChevronRight} from "react-icons/md";
import {FaFileCirclePlus} from "react-icons/fa6";
import {registerOrSortToOptionsDD} from "../../../layouts/data-panel/Cloud/Components/constants";
import Dropdown from "../../Dropdown";
import Input from "../../../common/Input";
import TextArea from "../../../common/TextArea";
import axios from "axios";
import {toast} from "react-toastify";
import {setUserSettings} from "../../../../_reducers/companySlice";


function getNoteType(type) {
    switch (String(type || "").trim().toLocaleLowerCase("el-GR")) {
        case "erp": return { label: "ERP", icon: <GrCloudSoftware size={20} />, theme: "erp" };
        case "eταμειακή": return { label: "eΤαμειακή", icon: <FaCashRegister size ={15} />, theme: "cash" };
        case "plugin": return { label: "Plugin", icon: <BsPlugin size={20} />, theme: "plugin" };
        default: return { label: type || "Άλλο", icon: <IoDocumentTextOutline size={20} />, theme: "other" };
    }
}

function NoteTypeBadge({ type }) {
    const meta = getNoteType(type);
    return (<span className={`rn-type-badge rn-type--${meta.theme}`}>{meta.icon}{meta.label}</span>);
}

function NoteTypeIcon({ type }) {
    const meta = getNoteType(type);
    return (
    <span className={`rn-type-icon rn-type--${meta.theme}`} aria-label={meta.label} title={meta.label}>
      {meta.icon}
    </span>
    );
}

const sorted = (items = []) => [...items].sort((a, b) => moment(b?.date).diff(moment(a?.date)) || String(b?.id ?? "").localeCompare(String(a?.id ?? "")));
const formatDate = (iso, options = {}) => {
    if (!iso) return "";
    const date = moment(iso);
    if (!date.isValid()) return "";
    return new Intl.DateTimeFormat("el-GR", options).format(date.toDate());
};
const toLines = (value) => value.split('\n').map((s) => s.trim().replace(/^[-•]\s*/, '')).filter(Boolean);

const AddReleaseNoteForm = ({ onSave, isSaving, setEditorOpen }) => {
    const [date, setDate] = useState(moment());
    const [type, setType] = useState("ERP");
    const [title, setTitle] = useState("");
    const [summary, setSummary] = useState("");
    const [newItems, setNewItems] = useState("");
    const [improvements, setImprovements] = useState("");
    const [fixes, setFixes] = useState("");
    const [error, setError] = useState("");

    const saveReleaseNote = async () => {
        if (isSaving) return;
        setError("");
        const sections = [
            {type: "new", title: "Νέες δυνατότητες", items: toLines(newItems)},
            {type: "improved", title: "Βελτιώσεις", items: toLines(improvements)},
            {type: "fixed", title: "Διορθώσεις", items: toLines(fixes)},
        ].filter((section) => section.items.length > 0);

        if (!date || !title.trim()) {
            return setError("Συμπληρώστε ημερομηνία και τίτλο.");
        }
        if (!sections.length) {
            return setError("Προσθέστε τουλάχιστον μία αλλαγή.");
        }
        const note = {id: `note-${type}-${Date.now()}`, date, type, title: title.trim(), summary: summary.trim(), sections};

        try {
            await onSave(note);
        } catch (err) {
            setError(err?.message || "Η αποθήκευση απέτυχε.");
        }
    }

    const typeOptions = [{value: "ERP", label: "ERP"}, {value: "eΤαμειακή", label: "eΤαμειακή"}, {value: "Plugin", label: "Plugin"}];

    return (
        <div id="rn-add-note-form" className="rn-editor-form">
            <div className="rn-editor-scroll">
                <div className="rn-editor-intro">
                    <h3>Νέα ενημέρωση</h3>
                    <p>Καταχωρίστε τις αλλαγές ανά ημερομηνία, χωρίς αριθμό έκδοσης.</p>
                </div>

                <div className="rn-form-grid">
                    <DateBox
                        name={"date"}
                        label={"Ημερομηνία"}
                        classes={"fix-date-box"}
                        selected={date}
                        allowOtherYears={true}
                        onChange={(e) => setDate(e)}
                    />
                    <Dropdown
                        name={"type"}
                        label={"Πρόγραμμα"}
                        options={typeOptions}
                        defaultValue={typeOptions.filter((el) => type === el.value)}
                        onChange={(e) => setType(e.value)}
                    />
                </div>

                <Input
                    name={"title"}
                    type={"text"}
                    label={"Τίτλος"}
                    value={title}
                    placeholder={"Τι άλλαξε;"}
                    className={"mb-2"}
                    onChange={(e) => setTitle(e.target.value)}
                />
                <TextArea
                    placeholder="Περιγραφή της ενημέρωσης..."
                    label="Σύντομη περιγραφή"
                    type="text"
                    rows="3"
                    value={summary}
                    onChange={(e) => setSummary(e.target.value)}
                />

                <p className="rn-field-hint">Μία αλλαγή ανά γραμμή. Οι κενές κατηγορίες δεν εμφανίζονται.</p>

                <TextArea
                    placeholder="Νέα λειτουργία..."
                    label="Νέες δυνατότητες"
                    type="text"
                    rows="3"
                    value={newItems}
                    onChange={(e) => setNewItems(e.target.value)}
                />

                <TextArea
                    placeholder="Βελτίωση απόδοσης..."
                    label="Βελτιώσεις"
                    type="text"
                    rows="3"
                    value={improvements}
                    onChange={(e) => setImprovements(e.target.value)}
                />

                <TextArea
                    placeholder="Διόρθωση προβλήματος..."
                    label="Διορθώσεις σφαλμάτων"
                    type="text"
                    rows="3"
                    value={fixes}
                    onChange={(e) => setFixes(e.target.value)}
                />
            </div>
            {error && <div className="rn-form-error" role="alert">{error}</div>}
            <div className="rn-editor-footer pt-3">
                <Button variant="outline-danger" onClick={() => setEditorOpen(false)} disabled={isSaving}>
                    Ακύρωση
                </Button>
                <Button variant="primary" onClick={saveReleaseNote} disabled={isSaving}>
                    {isSaving && (
                        <Spinner animation="border" size="sm" className="mr-2"/>
                    )}
                    {isSaving ? "Αποθήκευση..." : "Δημοσίευση ενημέρωσης"}
                </Button>
            </div>
        </div>
    );
}


const ReleaseNotesModal = () => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const [notes, setNotes] = useState(() => sorted([]));
    const [selectedId, setSelectedId] = useState(() => sorted([])[0]?.id ?? null);
    const [search, setSearch] = useState('');
    const [editorOpen, setEditorOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const dialogRef = useRef(null);
    const isOpen = useSelector((state) => state.ACTIVITY.showReleaseNotesModal);
    const currentUser = getCurrentUser();
    const [expandedMonth, setExpandedMonth] = useState([]);

    const filteredNotes = useMemo(() => sorted(notes).filter((note) =>
        `${note.title} ${moment(note.date).format("DD/MM/YYYY")} ${note.type} ${note.summary ?? ''}`.toLowerCase().includes(search.toLowerCase())
    ), [notes, search]);

    const groupedNotes = useMemo(() => {
        const groups = new Map();
        for (const note of filteredNotes) {
            const month = formatDate(note.date, { month: "long", year: "numeric" });
            if (!groups.has(month)) groups.set(month, []);
            groups.get(month).push(note);
        }
        return Array.from(groups.entries());
    }, [filteredNotes]);

    useEffect(() => {
        setExpandedMonth(groupedNotes.map(([month]) => month));
    }, [groupedNotes]);

    const sectionMeta = {
        new: {
            title: "Νέες δυνατότητες",
            icon: <IoSparkles size={20} />
        },
        improved: {
            title: "Βελτιώσεις",
            icon: <IoMdTrendingUp size={20} />
        },
        fixed: {
            title: "Διορθώσεις",
            icon: <FaBug size={20} />
        }
    };

    const fetchNotes = async () => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_API_URL2}/user/release-notes`, {headers: { "Content-Type": "application/json" }});
            if (res.data && res.data.status === "200" && res.data.data) {
                const next = sorted(res.data.data);
                setNotes(next);
                setSelectedId((current) => next.find((n) => n.id === current)?.id ?? next[0]?.id ?? null);
            }
        } catch (err) {
            console.error("Σφάλμα κατά την ανάκτηση ενημερώσεων", err);
            toast.error("Σφάλμα κατά την ανάκτηση ενημερώσεων!")
        }
    }

    const saveUserSeenNote = async () => {
        try {
            const res = await axios.post(`${process.env.REACT_APP_API_URL2}/user/save-seen-release-note`, {noteId: sorted(notes)[0]?.id}, {headers: { "Content-Type": "application/json" }});
            if (res.data.status === "200" && res.data.data) {
                localStorage.setItem("user", JSON.stringify(res.data.data));
                toast.success("Επιτυχής Ενημέρωση!");
                handleClose();
            }
        } catch (err) {
            console.error(err);
            toast.error("Σφάλμα κατά την ενημέρωση στοιχείων!");
        }
    }

    useEffect(() => {
        if (!isOpen) return;
        fetchNotes();
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        dialogRef.current?.focus();
        function onKeyDown(e) {
            if (e.key === 'Tab') {
                const root = dialogRef.current;
                if (!root) return;
                const focusable = [...root.querySelectorAll('button:not([disabled]), input:not([disabled]), textarea:not([disabled]), [tabindex="0"]')]
                    .filter((el) => el.getClientRects().length && (!editorOpen || Boolean(el.closest('.rn-editor'))));
                if (!focusable.length) return;
                const first = focusable[0], last = focusable[focusable.length - 1];
                if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
                else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
            }
        }
        document.addEventListener('keydown', onKeyDown);
        return () => { document.body.style.overflow = previousOverflow; document.removeEventListener('keydown', onKeyDown); };
    }, [isOpen, editorOpen]);


    const active = filteredNotes.find((note) => note.id === selectedId) || filteredNotes[0] || null;

    const saveNote = async (note) => {
        setIsSaving(true);
        try {
            const saveNote = await axios.post(`${process.env.REACT_APP_API_URL2}/user/release-notes`, note, {headers: { "Content-Type": "application/json" }});
            if (saveNote?.data?.status === "200") {
                setNotes((prev) => sorted([note, ...prev]));
                setSelectedId(note.id);
                setSearch('');
                setEditorOpen(false);
                toast.success("Επιτυχής δημιουργία ενημέρωσης!");
            } else {
                toast.error("Αποτυχία δημιουργίας καταχώρησης!");
            }
        } catch (error) {
            console.log(error);
            toast.error("Αποτυχία δημιουργίας καταχώρησης!");
        } finally {
            setIsSaving(false);
        }
    }

    const handleClose = () => {
        if (isSaving) return;
        setEditorOpen(false);
        dispatch(setShowReleaseNotesModal(false));
    };

    const newNoteIds = useMemo(() => {
        const allNotes = sorted(notes);
        if (!currentUser?.seenReleaseNote) {
            return new Set(allNotes.map(note => String(note.id)));
        }
        const seenIndex = allNotes.findIndex(note => String(note.id) === String(currentUser?.seenReleaseNote));
        if (seenIndex === -1) return new Set();
        return new Set(allNotes.slice(0, seenIndex).map(note => String(note.id)));
    }, [notes, currentUser]);

return (
<Modal animation={true} show={isOpen} onHide={handleClose} backdrop={isSaving ? "static" : true} keyboard={!isSaving} centered className="rn-bootstrap-modal" dialogClassName="rn-modal-dialog">
    <Modal.Header closeButton={!isSaving} className="rn-modal-header">
    <Modal.Title className="rn-modal-title" onClick={() => setEditorOpen(false)}>
        <span>Σημειώσεις Έκδοσης Εφαρμογών Primer Software</span>
    </Modal.Title>
    </Modal.Header>
    <Modal.Body className="rn-modal-body">
        {editorOpen ? (
            <AddReleaseNoteForm onSave={saveNote} isSaving={isSaving} setEditorOpen={setEditorOpen} />
        ) : (
            <div className="rn-layout">
                <aside className="rn-sidebar">
                    <div className="rn-sidebar-heading">
                        <div className="rn-sidebar-heading-top">
                            <h2>Ιστορικό ενημερώσεων</h2>
                            <span className="rn-count">{notes.length}</span>
                        </div>
                        <div className="rn-search mt-3">
                            <CiSearch size={16} />
                            <input
                                type="search"
                                value={search}
                                onChange={(event) => setSearch(event.target.value)}
                                placeholder="Αναζήτηση..."
                                aria-label="Αναζήτηση ενημερώσεων"
                            />
                        </div>
                    </div>

                    <nav className="rn-timeline" aria-label="Ημερομηνίες ενημερώσεων">
                        {groupedNotes.length === 0 && (
                            <p className="rn-no-results">Δεν βρέθηκαν ενημερώσεις.</p>
                        )}

                        <Accordion alwaysOpen activeKey={expandedMonth} onSelect={(key) => setExpandedMonth(key)} className="rn-month-accordion" flush>
                            {groupedNotes.map(([month, monthNotes]) => (
                                <Accordion.Item eventKey={month} key={month} className="rn-month-item">
                                    <Accordion.Header>
                                        <span className="rn-month-name">{month}</span>
                                        <span className="rn-month-count">{monthNotes.length}</span>
                                    </Accordion.Header>
                                    <Accordion.Body>
                                        {monthNotes.map((note) => {
                                            const isNew = newNoteIds.has(String(note.id));
                                            return (
                                            <button
                                                type="button"
                                                key={note.id}
                                                className={`rn-date-item ${active?.id === note.id ? "is-active" : ""}`}
                                                onClick={() => setSelectedId(note.id)}
                                                aria-current={active?.id === note.id ? "date" : undefined}>
                                                <NoteTypeIcon type={note.type} />
                                                <span className="rn-date-item-text">
                                                    <strong>{formatDate(note.date, {day: "numeric", month: "short", year: "numeric"})}</strong>
                                                    <small>{note.type}</small>
                                                    <small>{note.title}</small>
                                                </span>
                                                {isNew && (
                                                    <span className="rn-new-badge">Νέο</span>
                                                )}
                                                <IoChevronForwardSharp size={14} className="rn-item-chevron"/>
                                            </button>
                                        )})}
                                    </Accordion.Body>
                                </Accordion.Item>
                            ))}
                        </Accordion>
                    </nav>
                </aside>

                <div className="rn-main">
                    {active ? (
                        <div className="rn-content-scroll" key={active.id}>
                            <div className="rn-hero">
                                <div className="rn-release-meta">
                                    <span className="rn-release-date"><CiCalendarDate size={20} />{formatDate(active.date, { day: "numeric", month: "long", year: "numeric" })}</span>
                                    <NoteTypeBadge type={active.type} />
                                </div>
                                <h1>{active.title}</h1>
                                <p>{active.summary}</p>
                            </div>
                            <div className="rn-divider" />
                            <div className="rn-sections">
                                {(active.sections || []).map((section, index) => {
                                    const kind = sectionMeta[section.type] ? section.type : "improved";
                                    return (
                                        <section className="rn-section" key={`${section.type}-${index}`}>
                          <span className={`rn-section-icon rn-${kind}`}>{sectionMeta[kind].icon}</span>
                                            <div className="rn-section-body">
                                                <h2>{section.title || sectionMeta[kind].title}</h2>
                                                <ul>{(section.items || []).map((item, itemIndex) => <li key={itemIndex}>{item}</li>)}</ul>
                                            </div>
                                        </section>
                                    );
                                })}
                            </div>
                        </div>
                    ) : (
                        <div className="rn-empty">
                            <IoDocumentTextOutline size={37} />
                            <h3>Δεν υπάρχουν ενημερώσεις</h3>
                            <p>{search ? "Δοκιμάστε διαφορετική αναζήτηση." : "Δεν έχουν καταχωριστεί release notes."}</p>
                        </div>
                    )}
                </div>
            </div>
        )}
    </Modal.Body>
        {!editorOpen && (
            <Modal.Footer className="rn-modal-footer">
                {currentUser?.email === "dlalas@primer.gr" && !editorOpen && (
                    <Button variant="primary" onClick={() => setEditorOpen(true)}>Προσθήκη ενημέρωσης</Button>
                )}
                <Button variant="primary" onClick={() => saveUserSeenNote()}>Ενημερώθηκα</Button>
            </Modal.Footer>
            )}
</Modal>
);
}

export default ReleaseNotesModal;